import { parseJSON, differenceInCalendarMonths, add, intervalToDuration, addMonths } from "date-fns";
import { Box, Button, Calendar, ColumnConfig, DataTable, FormField, Heading, ResponsiveContext, Stack, Text, TextInput, Tip, Tip } from "grommet";
import { flatten, uniq, orderBy, get, groupBy, mapValues, values } from "lodash";
import React from "react";
import { Route, Routes, useParams } from "react-router-dom";
import { DateTimePicker, FormatDateTime } from "../components/DateTimePicker";
import DaySelector from "../components/DaySelector";
import { NavigationButton } from "../components/NavigationButton";
import LiveHub from "../general/LiveHub";
import { StatIcon } from "../stats/StatIcon";
import { StatValue } from "../stats/StatRenderer";
import { getStats, StatViewModel } from "../stats/StatsStore";
import { SeasonMatchLine } from "./SeasonMatchLine";
import { getSeason, SeasonMatchViewModel, SeasonRound, SeasonViewModel, TeamStatisticsViewModel } from "./SeasonsStore";
import * as chroma from "chroma-js";
import _ from "lodash";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { updateMatch } from "../matches/MatchesStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import StyledModal from "../components/StyledModel";

export interface SeasonMatchSchedule {
    roundNumber: number;
    days: { [key: string]: SeasonMatchViewModel[] };
}

export interface SeasonMatchScheduleByTimeSlot {
    [key: string]: SeasonMatchViewModel[];
}

export interface SeasonMatchScheduleByDay {
    [key: string]: SeasonMatchScheduleByTimeSlot;
}

export interface SeasonRoundsByDay {
    [key: string]: number;
}

export const SeasonScheduleWrapper = () => {
    let { id } = useParams<{ id: string }>();
    const [season, setSeason] = React.useState({} as SeasonViewModel);

    React.useEffect(() => {
        resolve();
    }, []);

    const resolve = async () => {
        let season = await getSeason(id);
        setSeason(season);
        setupSchedule(season.schedule.rounds, season);
    };

    React.useEffect(() => {}, []);

    // const [matchSchedule, setMatchSchedule] = React.useState([] as SeasonMatchViewModel[]);
    const [matchScheduleByDay, setMatchScheduleByDay] = React.useState({} as SeasonMatchScheduleByDay);
    const [roundsByDay, setRoundsByDay] = React.useState({} as SeasonRoundsByDay);

    const setupSchedule = (rounds: SeasonRound[], season: SeasonViewModel) => {
        // setColors(chroma.scale("RdYlBu").mode("lch").colors(rounds.length));

        let resolvedRounds = rounds.map((r) => ({ ...r, matches: r.matches.map((m) => ({ ...m, match: { ...season.matches.filter((sm) => sm.id == m.matchID)[0], startDate: m.matchStartDate } })) }));

        // orderBy(flatten(resolvedRounds[0].matches), (m) => parseJSON(m.match.startDate), ["asc"]);

        // setMatchSchedule(orderBy(flatten(resolvedRounds[0].matches), (m) => parseJSON(m.match.startDate), ["asc"]));
        // setMatchSchedule(resolvedRounds.map((r) => ({ ...r, days: groupBy(r.matches, (m) => FormatDateTime(parseJSON(m.matchStartDate), "yyyy/MM/dd")) })));

        let matchScheduleByDayAndTime = _(flatten(resolvedRounds.map((r) => r.matches)))
            .groupBy((m) => FormatDateTime(parseJSON(m.matchStartDate), "yyyy/MM/dd"))
            .mapValues((matches) =>
                _(matches)
                    .groupBy((m) => FormatDateTime(parseJSON(m.matchStartDate), "HH:mm"))
                    .value()
            )
            .value();

        setMatchScheduleByDay(matchScheduleByDayAndTime);
        setRoundsByDay(
            mapValues(
                groupBy(flatten(rounds.map((r) => r.matches.map((m) => ({ date: m.matchStartDate, roundNumber: r.roundNumber })))), (m) => FormatDateTime(parseJSON(m.date), "yyyy/MM/dd")),
                (m) => m[0].roundNumber
            )
        );
    };

    // const [colors, setColors] = React.useState([] as string[]);

    return (
        <LiveHub entities={[id]} handleUpdate={resolve}>
            <Box fill pad="medium">
                <Heading>
                    {season.league?.name} {season.name}
                </Heading>
                {season.id && <SeasonSchedule season={season} matchScheduleByDay={matchScheduleByDay} roundsByDay={roundsByDay}></SeasonSchedule>}
            </Box>
        </LiveHub>
    );
};

const SeasonSchedule = ({
    disabled,
    season,
    matchScheduleByDay,
    roundsByDay,
    onChange,
}: {
    disabled?: boolean;
    season: SeasonViewModel;
    matchScheduleByDay: SeasonMatchScheduleByDay;
    roundsByDay: SeasonRoundsByDay;
    onChange?: () => void;
}) => {
    const months = differenceInCalendarMonths(parseJSON(season.endDate), parseJSON(season.startDate));

    const handleDrop = async (droppedItem) => {
        if (droppedItem.source && droppedItem.destination) {
            let [round, seasonMatchID] = droppedItem.draggableId.split(":");

            let seasonMatch = season.schedule.rounds
                .map((r) => r.matches)
                .flat()
                .filter((m) => m.seasonMatchID == parseInt(seasonMatchID))[0];

            if (seasonMatch.match) {
                await updateMatch({
                    id: seasonMatch.match.id,
                    startDate: add(new Date(droppedItem.destination.droppableId), {}).toISOString(),
                    isPublic: seasonMatch.match.isPublic,
                });
                onChange && onChange();
            }
        }
    };

    const [showMatchModal, setShowMatchModal] = React.useState(false);

    const [selectedMatch, setSelectedMatch] = React.useState({} as SeasonMatchViewModel);

    const selectMatch = (match) => {
        setSelectedMatch(match);
        setShowMatchModal(true);
        setNewStartDate(match.matchStartDate);
    };

    const [newStartDate, setNewStartDate] = React.useState("");

    const updateSeasonMatch = async () => {
        if (selectedMatch.match) {
            await updateMatch({
                id: selectedMatch.match.id,
                startDate: newStartDate,
                isPublic: selectedMatch.match.isPublic,
            });
            onChange && onChange();
            setShowMatchModal(false);
        }
    };

    return (
        <>
            <Box direction="row" align="center" justify="center" gap="medium" wrap pad={{ top: "small" }}>
                <NavigationButton href={`calendar`} text={"Calendar"}></NavigationButton>
                <NavigationButton href={`list`} text={"List"}></NavigationButton>
            </Box>
            <Routes>
                <Route
                    path="calendar"
                    element={
                        <>
                            <DragDropContext onDragEnd={handleDrop}>
                                {Array.from({ length: months + 1 }).map((m, i) => (
                                    <Calendar
                                        key={"calendar" + i}
                                        animate={false}
                                        daysOfWeek
                                        fill
                                        showAdjacentDays={false}
                                        date={addMonths(parseJSON(season.startDate), i).toUTCString()}
                                        header={({ date: currentDate, locale }) => (
                                            <Heading level={3} margin="none">
                                                {currentDate.toLocaleDateString(locale, {
                                                    month: "long",
                                                    year: "numeric",
                                                })}
                                            </Heading>
                                        )}
                                    >
                                        {({ date, day, isSelected }) => {
                                            const matches = _(matchScheduleByDay?.[FormatDateTime(date, "yyyy/MM/dd")]).values().flatten().value();
                                            const round = roundsByDay?.[FormatDateTime(date, "yyyy/MM/dd")];
                                            return (
                                                <Droppable droppableId={date.toString()}>
                                                    {(provided, snapshot) => (
                                                        <Box
                                                            {...provided.droppableProps}
                                                            ref={provided.innerRef}
                                                            key={FormatDateTime(date, "yyyy/MM/dd")}
                                                            pad="small"
                                                            border={{ color: matches.length > 0 ? "brand" : "", size: "2px", side: "top" }}
                                                        >
                                                            <Text>{day}</Text>
                                                            {matches &&
                                                                orderBy(matches, (m) => m.matchStartDate).map((match, i) => (
                                                                    <Box key={match.seasonMatchID} direction="row" fill align="center">
                                                                        <Draggable draggableId={round + ":" + match.seasonMatchID.toString()} index={i} isDragDisabled={disabled}>
                                                                            {(provided, snapshot) => (
                                                                                <Box
                                                                                    fill={"horizontal"}
                                                                                    ref={provided.innerRef}
                                                                                    {...provided.draggableProps}
                                                                                    {...provided.dragHandleProps}
                                                                                    direction="row"
                                                                                    align="center"
                                                                                    gap="small"
                                                                                    pad={"xsmall"}
                                                                                    border={{ size: "small", color: snapshot.isDragging ? "brand" : "transparent" }}
                                                                                >
                                                                                    <Stack anchor="bottom" fill>
                                                                                        <SeasonMatchLine
                                                                                            onClick={() => selectMatch(match)}
                                                                                            seasonMatch={match}
                                                                                            teams={season.participants.map((p) => p.team)}
                                                                                        ></SeasonMatchLine>
                                                                                        <Text size="small">{FormatDateTime(parseJSON(match.matchStartDate), "HH:mm")}</Text>
                                                                                    </Stack>
                                                                                </Box>
                                                                            )}
                                                                        </Draggable>
                                                                        {matches.filter(
                                                                            (m) => m.matchStartDate == match.matchStartDate && !m.participantTeamIDs.every((p) => !match.participantTeamIDs.includes(p))
                                                                        ).length > 1 && (
                                                                            <Tip content={<Text>Team is already playing at this time</Text>}>
                                                                                <Box>
                                                                                    <FontAwesomeIcon color="red" icon={["fad", "exclamation-circle"]}></FontAwesomeIcon>
                                                                                </Box>
                                                                            </Tip>
                                                                        )}
                                                                    </Box>
                                                                ))}
                                                            {provided.placeholder && <Box background={{ color: "background" }}>{provided.placeholder}</Box>}
                                                        </Box>
                                                    )}
                                                </Droppable>
                                            );
                                        }}
                                    </Calendar>
                                ))}
                            </DragDropContext>
                        </>
                    }
                />
                <Route
                    path="list"
                    element={
                        <>
                            {Object.entries(matchScheduleByDay)
                                .sort(([dayA], [dayB]) => (dayA > dayB ? 1 : -1))
                                .map(([day, timeSlots]) => (
                                    <Box key={day} gap="small">
                                        <Text>{day}</Text>
                                        {Object.entries(timeSlots).map(([time, matches]) => (
                                            <Box direction="row" key={day + " " + time} gap="small">
                                                {matches.map((match) => (
                                                    <Stack key={match.seasonMatchID} anchor="bottom" style={{ minWidth: "180px" }}>
                                                        <SeasonMatchLine seasonMatch={match} teams={season.participants.map((p) => p.team)}></SeasonMatchLine>
                                                        <Text size="small">{FormatDateTime(parseJSON(match.matchStartDate), "HH:mm")}</Text>
                                                    </Stack>
                                                ))}
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                        </>
                    }
                />
            </Routes>
            <StyledModal isOpen={showMatchModal} setIsOpen={() => setShowMatchModal(false)}>
                <Box gap="small">
                    <SeasonMatchLine seasonMatch={selectedMatch} teams={season.participants.map((p) => p.team)}></SeasonMatchLine>
                    <DateTimePicker step={300} value={newStartDate} onChange={setNewStartDate}></DateTimePicker>
                    <Button primary label="Save" onClick={updateSeasonMatch}></Button>
                </Box>
            </StyledModal>
        </>
    );
};

export default SeasonSchedule;
